import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import type { FC } from 'react';
import { useNavigate } from 'react-router';
import { ownerContributorAndViewerIds } from 'src/components/form';
import { useUpdateInternalAudit } from 'src/hooks/mutations/internal-audit';
import {
  useGetBusinessAreas,
  useGetInternalAuditById,
  useGetInternalAuditEntitiesRegister,
} from 'src/hooks/queries';
import {
  getContributors,
  getOwners,
  getViewers,
} from 'src/rbac/contributorHelper';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';
import { useGetDetailParentPath } from 'src/routes/useGetDetailParentPath';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsFormPrefillEnabled } from '@/hooks/useIsFormPrefillEnabled';

import InternalAuditForm from '../../../forms/InternalAuditForm';
import type { InternalAuditFormDataFields } from '../../../forms/internalAuditSchema';
import { defaultValues } from '../../../forms/internalAuditSchema';

const Tab: FC = () => {
  const internalAuditId = useGetGuidParam('internalAuditId');
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const formPrefillEnabled = useIsFormPrefillEnabled();
  const navigate = useNavigate();

  const {
    data,
    error,
    loading: loadingInternalAudit,
    refetch: refetchInternalAudit,
  } = useGetInternalAuditById({ queryArgs: { internalAuditId } });

  const { refetch: refetchInternalAuditsRegister } =
    useGetInternalAuditEntitiesRegister({ queryArgs: {} });

  if (error) {
    throw error;
  }

  const { data: businessAreasData } = useGetBusinessAreas({ queryArgs: {} });

  const internalAudit = data?.internal_audit_entity[0];
  const { hasPermission: canEdit, loading: canEditLoading } =
    useHasPermissionQuery('update:internal_audit_entity', internalAudit);
  const parentPath = useGetDetailParentPath(internalAuditId);
  const { updateInternalAudit } = useUpdateInternalAudit();

  const onSave = async (data: InternalAuditFormDataFields) => {
    if (!internalAudit) {
      throw new Error('Missing internal audit');
    }
    // The legacy Apollo path requires a client-resolved BusinessAreaId; the
    // tRPC path re-derives it server-side from the free-text BusinessArea, so
    // this is only load-bearing behind the flag being off.
    const businessAreaChanged =
      internalAudit.businessArea?.Title !== data.BusinessArea;

    let businessAreaId = internalAudit.businessArea!.Id;
    if (businessAreaChanged) {
      const existingBusinessAreaId = businessAreasData?.business_area?.find(
        (c) => c.Title === data.BusinessArea
      )?.Id;
      if (existingBusinessAreaId) {
        businessAreaId = existingBusinessAreaId;
      } else {
        businessAreaId = crypto.randomUUID();
      }
    }

    await updateInternalAudit({
      Id: internalAudit.Id,
      BusinessArea: data.BusinessArea ?? '',
      DepartmentTypeIds: data.departments?.map((d) => d.DepartmentTypeId) || [],
      TagTypeIds: data.tags?.map((t) => t.TagTypeId) || [],
      Title: data.Title,
      Description: data.Description,
      CustomAttributeData: data.CustomAttributeData || undefined,
      OriginalTimestamp:
        internalAudit.ModifiedAtTimestamp ?? internalAudit.CreatedAtTimestamp!,
      BusinessAreaId: businessAreaId,
      ...ownerContributorAndViewerIds(data),
    });
    refetchInternalAuditsRegister();
    refetchInternalAudit();
  };
  const onDismiss = () => navigate(parentPath);

  return (
    <InternalAuditForm
      readOnly={!canEdit || canEditLoading || loadingInternalAudit}
      values={{
        ...defaultValues,
        ...internalAudit,
        Title: internalAudit?.Title ?? '',
        Description: internalAudit?.Description ?? '',
        BusinessArea: internalAudit?.businessArea?.Title ?? '',
        Owners: getOwners(internalAudit),
        Contributors: getContributors(internalAudit),
        Viewers: getViewers(internalAudit),
      }}
      onSave={onSave}
      onDismiss={onDismiss}
      publishToolbarState={objectToolbarEnabled}
      publishActiveForm={formPrefillEnabled}
      activeFormRecordId={internalAuditId}
    />
  );
};

export default Tab;
